import { Controller } from "@hotwired/stimulus";

// Step 1 of the creation wizard (design « GO4LEARN Studio — Créer quiz et ressources »,
// boards Q1 and R1, 2026-10-19): Pays · Espace · Classe · Matière · Chapitre.
//
// Pays and Espace change the programmes served: the page reloads with them (the server
// builds the cascade). Classe → Matière → Chapitre are filtered here, from the options the
// server rendered for the space (each option carries its parent in `data-parent`), and the
// side panel shows the chosen chapter (`data-chapter-panel`).
//
// For a resource the chapters are a checklist (one, several, or the whole subject): only the
// rows of the chosen subject show, and a change of subject unticks the others — a resource
// covers chapters of ONE subject.
export default class extends Controller {
  static targets = ["scopeForm", "classSelect", "subjectSelect", "chapterSelect",
                    "chapterRow", "panel", "wholeSubject", "coverage", "chapterList"];

  connect() {
    this.filterSubjects();
  }

  reload() {
    this.scopeFormTarget.requestSubmit();
  }

  filterSubjects() {
    if (!this.hasSubjectSelectTarget) return;
    this.keepOptions(this.subjectSelectTarget, this.classSelectTarget?.value);
    this.filterChapters();
  }

  filterChapters() {
    const subject = this.hasSubjectSelectTarget ? this.subjectSelectTarget.value : null;
    if (this.hasChapterSelectTarget) this.keepOptions(this.chapterSelectTarget, subject);
    this.chapterRowTargets.forEach((row) => {
      const mine = row.dataset.parent === subject;
      row.hidden = !mine;
      if (!mine) row.querySelectorAll("input").forEach((input) => { input.checked = false; });
    });
    this.wholeSubjectTargets.forEach((input) => { input.value = subject || ""; });
    this.showCoverage();
    this.showPanel();
  }

  // « La ressource couvre » — one chapter, several, or the whole subject.
  showCoverage() {
    if (!this.hasCoverageTarget || !this.hasChapterListTarget) return;
    const chosen = this.coverageTargets.find((input) => input.checked)?.value;
    const whole = chosen === "subject";
    this.chapterListTarget.hidden = whole;
    this.chapterRowTargets.forEach((row) => {
      const input = row.querySelector("input");
      if (!input) return;
      input.type = chosen === "one" ? "radio" : "checkbox";
      if (whole) input.checked = false;
    });
    this.wholeSubjectTargets.forEach((input) => { input.disabled = !whole; });
  }

  showPanel() {
    const key = this.hasChapterSelectTarget
      ? this.chapterSelectTarget.value
      : this.chapterRowTargets.find((row) => !row.hidden && row.querySelector("input:checked"))?.dataset.key;
    this.panelTargets.forEach((panel) => { panel.hidden = panel.dataset.chapterPanel !== key; });
  }

  // Keeps the options whose parent is `parent`, rebuilt from the full list read once at
  // connect: SlimSelect rewrites the native options when told new data, so hiding them in
  // place would lose the others for good.
  keepOptions(select, parent) {
    this.fullLists ||= new Map();
    if (!this.fullLists.has(select)) {
      this.fullLists.set(select, Array.from(select.options).map((option) => ({
        text: option.text.trim(), value: option.value, parent: option.dataset.parent,
      })));
    }
    const kept = this.fullLists.get(select).filter((option) => !option.parent || option.parent === parent);
    const previous = select.value;
    select.replaceChildren(...kept.map((option) => {
      const element = new Option(option.text, option.value);
      if (option.parent) element.dataset.parent = option.parent;
      return element;
    }));
    select.value = kept.some((option) => option.value === previous) ? previous : (kept[0]?.value ?? "");
    this.syncSlimSelect(select);
  }

  // GO4LEARN dresses its selects with SlimSelect (`utils/g4l_select.js`), which draws its own
  // list: it is told the kept options, or it would still offer the filtered-out ones.
  syncSlimSelect(select) {
    const slim = select.g4lSelect;
    if (!slim) return;
    slim.setData(Array.from(select.options).map((option) => ({
      text: option.text, value: option.value, selected: option.value === select.value,
    })));
  }
}
